import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import usePreferredDateFormat from '@/hooks/usePreferredDateFormat';
import { Alert, Text } from '@payaca/ambrosia';
import { DateFormats } from '@payaca/helpers/internationalHelper';
import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { format } from 'date-fns';
import { FC } from 'react';
import { SentProposalStatus } from './sentProposalStatus';

type SummaryLine = { iconName: TIconName; text: string };

type SentProposalStatusSummaryProps = {
  proposalId: string;
  status: SentProposalStatus;
};

/** Either part can be null on a lead's Contact, so neither is assumed. */
const describeRecipient = (recipient: {
  name?: string | null;
  email?: string | null;
}): string => {
  const { name, email } = recipient;
  if (name && email) return `Sent to ${name} (${email})`;
  return `Sent to ${name ?? email}`;
};

/**
 * What happened to the Proposal, from what its query already carries. Note
 * `acceptedAt` covers both the Customer accepting and a Provider recording it
 * on their behalf, so neither is claimed here.
 */
const SentProposalStatusSummary: FC<SentProposalStatusSummaryProps> = (
  props
) => {
  const { proposalId, status } = props;

  const { proposal } = useGetProposal(proposalId);
  const dateFormat = usePreferredDateFormat(DateFormats.MID);

  const formatDate = (value: string | null | undefined) =>
    value ? format(new Date(value), dateFormat) : null;

  const recipient = proposal?.customerContact;
  const rejectedAddress = recipient?.email;

  if (status === 'bounced') {
    return (
      <div className="border-b border-gray-200 p-3.5">
        {/* Both lines are plain Text, so they inherit the Alert's colour —
            `supporting` sets its own grey, which would not read on red. */}
        <Alert colour="red" iconName="alert-triangle.3">
          <Text>This proposal could not be delivered.</Text>

          {rejectedAddress && (
            <Text>{rejectedAddress} — the recipient address was rejected.</Text>
          )}
        </Alert>
      </div>
    );
  }

  const lines: SummaryLine[] = [];

  lines.push({
    iconName: 'mail-01.3',
    // A Proposal marked as sent out of band has no pinned Contact, because
    // nothing was emailed.
    text:
      recipient && (recipient.name || recipient.email)
        ? describeRecipient(recipient)
        : 'Marked as sent, not emailed',
  });

  const sentOn = formatDate(proposal?.sentAt);
  if (sentOn) lines.push({ iconName: 'clock.3', text: `Sent ${sentOn}` });

  // A decision outranks the bounce in the status, so the delivery failure
  // would otherwise vanish — while a resend still opens the correction form.
  const bouncedOn = formatDate(proposal?.bouncedAt);
  if (bouncedOn) {
    lines.push({
      iconName: 'alert-triangle.3',
      text: rejectedAddress
        ? `Delivery to ${rejectedAddress} bounced ${bouncedOn}`
        : `Delivery bounced ${bouncedOn}`,
    });
  }

  if (status === 'accepted') {
    const acceptedOn = formatDate(proposal?.acceptedAt);
    if (acceptedOn) {
      lines.push({
        iconName: 'check-circle.3',
        text: `Accepted ${acceptedOn}`,
      });
    }
  }

  if (status === 'declined') {
    const declinedOn = formatDate(proposal?.declinedAt);
    if (declinedOn) {
      lines.push({ iconName: 'x-circle.3', text: `Declined ${declinedOn}` });
    }
  }

  return (
    <ul
      role="list"
      aria-label="Proposal status"
      className="m-0 flex list-none flex-col gap-2 border-b border-gray-200 p-3.5"
    >
      {lines.map((line) => (
        <li key={line.text} className="flex items-center gap-2.5">
          <UntitledIcon
            name={line.iconName}
            className="size-4 shrink-0 text-gray-400"
          />

          <Text level="supporting">{line.text}</Text>
        </li>
      ))}
    </ul>
  );
};

export default SentProposalStatusSummary;
